import { FiChevronDown, FiX, FiCheck, FiActivity, FiLayers, FiAlertTriangle, FiNavigation, FiFileText, FiMapPin, FiMap } from "react-icons/fi";
import { SelectSearchFilter } from "../../../components/SelectSearchFilter";

type ViewMode = "zones" | "monitoring" | "emergency";
type PinTheme = "number" | "name" | "fullName";
type MapBackground = "streets" | "satellite";
type MonitoringStatus = "ok" | "attention" | "alert" | "critical" | "delivery_only";

const MONITORING_STATUS_OPTIONS: { value: MonitoringStatus; label: string; color: string; bgColor: string }[] = [
  { value: "ok", label: "0-15 dias (OK)", color: "#22C55E", bgColor: "#DCFCE7" },
  { value: "attention", label: "16-30 dias (Atenção)", color: "#EAB308", bgColor: "#FEF9C3" },
  { value: "alert", label: "31-45 dias (Alerta)", color: "#F97316", bgColor: "#FFEDD5" },
  { value: "critical", label: "+46 dias (Crítico)", color: "#EF4444", bgColor: "#FEE2E2" },
  { value: "delivery_only", label: "Apenas entregas", color: "#6B7280", bgColor: "#F3F4F6" },
];

interface Zone {
  id: string;
  name: string;
  status: string;
  color?: string;
}

interface FilterControlsProps {
  // Busca
  selectedCostumerId: string;
  onCostumerIdChange: (id: string) => void;

  // Zonas
  zones: Zone[];
  selectedZones: string[];
  onZonesChange: (zones: string[]) => void;
  zonesDropdownOpen: boolean;
  onZonesDropdownToggle: (open: boolean) => void;
  zonesDropdownRef: React.RefObject<HTMLDivElement>;

  // Status
  selectedStatuses: MonitoringStatus[];
  onStatusesChange: (statuses: MonitoringStatus[]) => void;
  statusDropdownOpen: boolean;
  onStatusDropdownToggle: (open: boolean) => void;
  statusDropdownRef: React.RefObject<HTMLDivElement>;

  // View Mode
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;

  // Pin Theme
  pinTheme: PinTheme;
  onPinThemeChange: (theme: PinTheme) => void;

  // Map Background
  mapBackground: MapBackground;
  onMapBackgroundChange: (bg: MapBackground) => void;

  // Actions
  onGeocodeAll: () => void;

  // Loading
  dataLoading: boolean;
}

export function FilterControls({
  selectedCostumerId,
  onCostumerIdChange,
  zones,
  selectedZones,
  onZonesChange,
  zonesDropdownOpen,
  onZonesDropdownToggle,
  zonesDropdownRef,
  selectedStatuses,
  onStatusesChange,
  statusDropdownOpen,
  onStatusDropdownToggle,
  statusDropdownRef,
  viewMode,
  onViewModeChange,
  pinTheme,
  onPinThemeChange,
  mapBackground,
  onMapBackgroundChange,
  onGeocodeAll,
  dataLoading,
}: FilterControlsProps) {
  const toggleZone = (zoneId: string) => {
    if (selectedZones.includes(zoneId)) {
      onZonesChange(selectedZones.filter(id => id !== zoneId));
    } else {
      onZonesChange([...selectedZones, zoneId]);
    }
  };

  const toggleStatus = (status: MonitoringStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusesChange(selectedStatuses.filter(s => s !== status));
    } else {
      onStatusesChange([...selectedStatuses, status]);
    }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 mb-6 transition-opacity duration-300 ${
      dataLoading ? 'opacity-50' : 'opacity-100'
    }`}>
      {/* Header do card de filtros */}
      <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4 rounded-t-2xl">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
          </svg>
          Filtros e Controles
        </h3>
      </div>

      <div className="p-6">
        {/* Linha 1: Busca, Zonas e Status */}
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          {/* Busca de Cliente */}
          <div className="flex-1 min-w-[200px]">
            <span className="text-sm font-semibold text-gray-700 block mb-2">
              Cliente
            </span>
            <SelectSearchFilter
              placeholder="Buscar cliente..."
              value={selectedCostumerId}
              onChange={onCostumerIdChange}
            />
          </div>

          {/* Filtro Multi-select de Zonas */}
          <div className="flex-1 min-w-[200px]">
            <span className="text-sm font-semibold text-gray-700 block mb-2">
              Zonas
            </span>
            <div className="relative" ref={zonesDropdownRef}>
              <button
                type="button"
                onClick={() => onZonesDropdownToggle(!zonesDropdownOpen)}
                className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-lg flex items-center justify-between hover:border-[#0085BD] transition-colors"
              >
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  {selectedZones.length === 0 ? (
                    <span className="text-gray-400 text-sm">
                      Todas as zonas
                    </span>
                  ) : (
                    <span className="text-sm text-gray-700">
                      {selectedZones.length} zona
                      {selectedZones.length > 1 ? "s" : ""} selecionada
                      {selectedZones.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <FiChevronDown
                  className={`text-gray-400 transition-transform ${
                    zonesDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Lista Dropdown */}
              {zonesDropdownOpen && (
                <div className="absolute z-[1001] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {selectedZones.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onZonesChange([])}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center gap-2"
                    >
                      <FiX size={14} />
                      Limpar seleção
                    </button>
                  )}
                  {zones
                    .filter(zone => zone.status === "A")
                    .map(zone => (
                      <button
                        key={zone.id}
                        type="button"
                        onClick={() => toggleZone(zone.id)}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                          selectedZones.includes(zone.id) ? "bg-blue-50" : ""
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selectedZones.includes(zone.id)
                              ? "border-[#0085BD] bg-[#0085BD]"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedZones.includes(zone.id) && (
                            <FiCheck className="text-white" size={12} />
                          )}
                        </div>
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: zone.color || "#0085BD" }}
                        />
                        <span className="text-gray-700">{zone.name}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Filtro Multi-select de Status de Monitoramento */}
          <div className="flex-1 min-w-[200px]">
            <span className="text-sm font-semibold text-gray-700 block mb-2">
              Status de Monitoramento
            </span>
            <div className="relative" ref={statusDropdownRef}>
              <button
                type="button"
                onClick={() => onStatusDropdownToggle(!statusDropdownOpen)}
                className="w-full h-[52px] px-4 bg-white border border-gray-200 rounded-lg flex items-center justify-between hover:border-[#0085BD] transition-colors"
              >
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  {selectedStatuses.length === 0 ? (
                    <span className="text-gray-400 text-sm">
                      Todos os status
                    </span>
                  ) : (
                    <span className="text-sm text-gray-700">
                      {selectedStatuses.length} status selecionado
                      {selectedStatuses.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <FiChevronDown
                  className={`text-gray-400 transition-transform ${
                    statusDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Lista Dropdown */}
              {statusDropdownOpen && (
                <div className="absolute z-[1001] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {selectedStatuses.length > 0 && (
                    <button
                      type="button"
                      onClick={() => onStatusesChange([])}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 border-b border-gray-100 flex items-center gap-2"
                    >
                      <FiX size={14} />
                      Limpar seleção
                    </button>
                  )}
                  {MONITORING_STATUS_OPTIONS.map(status => (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => toggleStatus(status.value)}
                      className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                        selectedStatuses.includes(status.value) ? "bg-blue-50" : ""
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selectedStatuses.includes(status.value)
                            ? "border-[#0085BD] bg-[#0085BD]"
                            : "border-gray-300"
                        }`}
                      >
                        {selectedStatuses.includes(status.value) && (
                          <FiCheck className="text-white" size={12} />
                        )}
                      </div>
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: status.color }}
                      />
                      <span className="text-gray-700">{status.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Linha 2: Modo de Visualização e Ações */}
        <div className="mt-4 flex flex-col md:flex-row md:items-center gap-4">
          {/* Toggle de Visualização - Switch Pill Animado com 3 opções */}
          <div className="flex-1">
            <div className="relative bg-gray-100 rounded-full p-1 h-[48px] flex items-center max-w-[500px]">
              {/* Indicador deslizante */}
              <div
                className="absolute h-[40px] bg-[#0085BD] rounded-full shadow-md transition-all duration-300 ease-in-out"
                style={{
                  width: "calc(33.333% - 4px)",
                  left: viewMode === "monitoring" ? "4px" : viewMode === "zones" ? "calc(33.333% + 2px)" : "calc(66.666% + 0px)",
                }}
              />
              <button
                type="button"
                onClick={() => onViewModeChange("monitoring")}
                className={`relative z-10 flex-1 h-full flex items-center justify-center gap-1.5 rounded-full transition-colors duration-300 ${
                  viewMode === "monitoring"
                    ? "text-white font-medium"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiActivity size={16} />
                <span className="text-xs sm:text-sm">Monitoramento</span>
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange("zones")}
                className={`relative z-10 flex-1 h-full flex items-center justify-center gap-1.5 rounded-full transition-colors duration-300 ${
                  viewMode === "zones"
                    ? "text-white font-medium"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiLayers size={16} />
                <span className="text-xs sm:text-sm">Por Zona</span>
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange("emergency")}
                className={`relative z-10 flex-1 h-full flex items-center justify-center gap-1.5 rounded-full transition-colors duration-300 ${
                  viewMode === "emergency"
                    ? "text-white font-medium"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiAlertTriangle size={16} />
                <span className="text-xs sm:text-sm">Em Emergência</span>
              </button>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onGeocodeAll}
              className="group flex items-center gap-2 px-4 h-[44px] rounded-full bg-gradient-to-r from-[#0085BD] to-[#00a8e8] text-white font-medium shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 text-sm"
            >
              <FiNavigation size={16} className="group-hover:animate-pulse" />
              <span className="hidden sm:inline">Geocodificar</span>
            </button>

            <a
              href="/tech-visit-reports/new"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 px-4 h-[44px] rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 text-sm"
            >
              <FiFileText
                size={16}
                className="group-hover:rotate-12 transition-transform"
              />
              <span className="hidden sm:inline">Novo Relatório</span>
            </a>
          </div>
        </div>

        {/* Linha 3: Controles de Aparência (discretos) */}
        <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-gray-100 pt-4">
          {/* Tema do Pin */}
          <div className="flex items-center gap-2">
            <FiMapPin className="text-gray-400" size={14} />
            <span className="text-xs text-gray-500">Pin:</span>
            <div className="bg-gray-100 rounded-full p-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => onPinThemeChange("number")}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-300 ease-in-out ${
                  pinTheme === "number"
                    ? "bg-gray-600 text-white font-medium shadow-sm scale-105"
                    : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                }`}
                title="Apenas número"
              >
                Nº
              </button>
              <button
                type="button"
                onClick={() => onPinThemeChange("name")}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-300 ease-in-out ${
                  pinTheme === "name"
                    ? "bg-gray-600 text-white font-medium shadow-sm scale-105"
                    : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                }`}
                title="Nome Resumido"
              >
                Resumido
              </button>
              <button
                type="button"
                onClick={() => onPinThemeChange("fullName")}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-300 ease-in-out ${
                  pinTheme === "fullName"
                    ? "bg-gray-600 text-white font-medium shadow-sm scale-105"
                    : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                }`}
                title="Nome Completo"
              >
                Completo
              </button>
            </div>
          </div>

          {/* Fundo do Mapa */}
          <div className="flex items-center gap-2">
            <FiMap className="text-gray-400" size={14} />
            <span className="text-xs text-gray-500">Mapa:</span>
            <div className="bg-gray-100 rounded-full p-1 flex items-center gap-1">
              <button
                type="button"
                onClick={() => onMapBackgroundChange("streets")}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-300 ease-in-out ${
                  mapBackground === "streets"
                    ? "bg-gray-600 text-white font-medium shadow-sm scale-105"
                    : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                }`}
              >
                Ruas
              </button>
              <button
                type="button"
                onClick={() => onMapBackgroundChange("satellite")}
                className={`px-3 py-1 rounded-full text-xs transition-all duration-300 ease-in-out ${
                  mapBackground === "satellite"
                    ? "bg-gray-600 text-white font-medium shadow-sm scale-105"
                    : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-200"
                }`}
              >
                Satélite
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
